export const SAVE_KEY = "voidgalaxy_save";

export const DEFAULT_STATS = {
  // Mining laser
  laserDamage: 8,
  laserRange: 95,
  rotSpeed: 0.1,
  bonusLoot: 0,
  beamWidth: 1,
  attractRange: 30,
  autoBreak: 0,
  smeltSpeed: 1,
  smeltYield: 1,          // multiplier on output quantity per smelt (e.g. 1.5 = +50% materials out)
  smeltCostMult: 1,       // multiplier on input quantities (e.g. 0.8 = 20% fewer materials needed)
  spawnRate: 1,
  debrisCap: 8,
  autoNavUnlocked: false,
  droneMax: 0,
  plasmaVisual: false,
  smeltQueueMax: 1,
  activeCardSlots: 1,
  echoFrequency: 0,      // additive bonus to echo spawn chance (0.10 = +10%)
  echoCurrencyBonus: 0,  // flat extra Echo Currency per event
  echoCardBoost: 0,      // additive multiplier to active echo card bonuses (0.10 = +10%)
  // Combat
  shipSpeed: 1.8,
  blasterDamage: 12,
  blasterFireRate: 30,   // frames between shots (lower = faster)
  blasterCount: 1,       // bullets per shot
  playerMaxHp: 100,
  shieldMax: 10,          // 0 = no shield until researched
  // Starmap
  fastTravelFree: false,  // true = fast-travel to visited nodes costs 0 fuel
  scannerCharges: 0,      // scanner uses per sector (0 = not unlocked)
  scannerReveal: 0,       // how many adjacent unvisited nodes are revealed per scan
  sectorDiversity: 0,     // 0–3, increases material_site / fuel_depot frequency
  // XP
  xpMult: 1,              // multiplier on all XP gains
  xpPerMine: 0,           // bonus flat XP per debris mined (added on top of base 8)
  xpPerKill: 0,           // bonus flat XP per enemy killed (on top of base 25*tier)
  xpLevelUpHeal: 0,       // HP restored on level-up (0 = no heal)
  xpLevelUpFuel: 0,       // fuel restored on level-up (0 = none)
  // Trade
  tradeCooldownHours: 24, // hours between shop resets (0 = no cooldown)
  tradeBuyMult: 1,        // multiplier on buy prices (< 1 = cheaper)
  tradeSellBonus: 0,      // added to SELL_RATIO base of 0.45
  tradeStockMult: 1,      // multiplier on shop stock quantities
  // Combat — advanced mechanics (combat.md)
  coreHeatMax: 100,       // max core heat; at max, blaster cannot fire until it cools
  coreHeatCoolRate: 0.5,  // heat dissipated per frame when not firing
  coreHeatPerShot: 1.5,   // heat added per blaster shot
  qteSliderWidth: 0.22,   // fraction of slider where "green zone" sits (AI Tracking widens)
  qteFrigateMitigation: 0, // 0–0.6; % of failed Sequence Intercept damage ignored (Firewall Patch)
  qteBreachSlow: 0,       // 0–0.5; multiplier reducing Precision Breach ring speed (Orbital Drill)
};

export function loadSave() {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d.visitedNodes)        d.visitedNodes        = new Set(d.visitedNodes);
    if (d.discoveredMaterials) d.discoveredMaterials = new Set(d.discoveredMaterials);
    // Echo Cards: boostedCards and claimedVoidRewards saved as arrays, hook converts to Sets
    if (!Array.isArray(d.boostedCards)) d.boostedCards = [];
    if (!Array.isArray(d.claimedVoidRewards)) d.claimedVoidRewards = [];
    return d;
  } catch {
    return null;
  }
}

// Starting fuel for a fresh save or new sector
export const FUEL_START = 30;
// Maximum fuel the player can carry (can be raised by research later)
export const FUEL_MAX   = 40;

// Travel fuel costs
export const FUEL_COST_ADJACENT  = 3;  // hop to an adjacent node
export const FUEL_COST_FAST      = 6;  // fast-travel to a previously visited node
export const FUEL_GAIN_DEPOT     = 15; // fuel restored at a fuel_depot node

// Starting Void-Creds balance for a new save
export const VC_START = 50;

export function writeSave(data) {
  try {
    const d = {
      ...data,
      visitedNodes:        [...data.visitedNodes],
      discoveredMaterials: [...(data.discoveredMaterials ?? [])],
      // Echo Cards system — serialize Sets to arrays
      boostedCards:        data.boostedCards        ? [...data.boostedCards]        : [],
      claimedVoidRewards:  data.claimedVoidRewards  ? [...data.claimedVoidRewards]  : [],
      lastOnline: Date.now(),
    };
    localStorage.setItem(SAVE_KEY, JSON.stringify(d));
  } catch { /* storage full / private mode */ }
}

// Format milliseconds as "Xh Ym" or "Ym Zs" or "Zs"
export function fmtDuration(ms) {
  if (!ms || ms <= 0) return "0s";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// Migrate old save format: convert flat `researched` array → researchLevels map.
// Each entry in the old array counts as level 1.
export function migrateResearchLevels(saved) {
  if (saved?.researchLevels) {
    const lvls = { ...saved.researchLevels };
    // Collapse old separate refinery IDs into single multi-level `refinery` node.
    // Each old refinery that was purchased counts as one level.
    const oldRefineries = ["basic_refinery", "refinery1", "refinery2", "refinery3", "refinery4", "refinery5"];
    let refineryLevel = lvls.refinery ?? 0;
    for (const id of oldRefineries) {
      if ((lvls[id] ?? 0) >= 1) { refineryLevel = Math.max(refineryLevel, oldRefineries.indexOf(id) + 1); }
      delete lvls[id];
    }
    if (refineryLevel > 0) lvls.refinery = Math.min(refineryLevel, 5);
    return lvls;
  }
  if (saved?.researched && Array.isArray(saved.researched)) {
    const levels = {};
    // Map old IDs to new IDs where renamed
    const idMap = {
      queue2: "queue", queue3: "queue", queue4: "queue", queue5: "queue", queue6: "queue",
      cardslots2: "riftconduit", cardslots3: "riftconduit", cardslots4: "riftconduit",
      riftconduit2: "riftconduit", riftconduit3: "riftconduit", riftconduit4: "riftconduit",
      basic_refinery: "refinery", refinery1: "refinery", refinery2: "refinery",
      refinery3: "refinery", refinery4: "refinery", refinery5: "refinery",
    };
    for (const id of saved.researched) {
      const mapped = idMap[id] ?? id;
      levels[mapped] = (levels[mapped] ?? 0) + 1;
    }
    return levels;
  }
  return {};
}

// XP required to reach the next level from `level` (1-indexed).
// Curve: 100 * level^1.4 — slow early, steeper late.
export function xpForLevel(level) {
  return Math.floor(100 * Math.pow(level, 1.4));
}

// Saves only the tutorial step (called more frequently to avoid losing progress)
export function writeTutorialStep(step) {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    const d = raw ? JSON.parse(raw) : {};
    d.tutorialStep = step;
    localStorage.setItem(SAVE_KEY, JSON.stringify(d));
  } catch { /* ignore */ }
}
